import { ensureDatadogClient, isDatadogEnabled } from './datadogClient';
import {
  getMessageFromError,
  isIgnoredDatadogErrorMessage,
  isWarningDatadogMessage,
} from './datadogFilters';

let hooksRegistered = false;

const CAPTURED_BUSINESS_ERROR_CODES = new Set([
  'add-payment-method-failed',
  'addToCart-failed',
  'accept-sale-failed',
  'auth-info-failed',
  'cancel-booking-failed',
  'create-setup-intent-failed',
  'create-listing-draft-failed',
  'create-stripe-user-failed',
  'fetch-stripe-customer-failed',
  'fetch-current-user-failed',
  'initial-message-send-failed',
  'initiate-order-failed',
  'load-data-failed',
  'manual-payment-multi-booking-order-failed',
  'refreshCartMaybe-failed',
  'reject-sale-failed',
  'refund-booking-after-delivered-failed',
  'signup-failed',
  'speculate-transaction-failed',
  'stripe-handle-card-payment-failed',
  'stripe-handle-card-setup-failed',
  'stripe-retrieve-payment-intent-failed',
  'update-listing-failed',
  'updateCart-failed',
  'updating-payment-method-failed',
]);

const normalizeSeverity = (severity) => {
  if (typeof severity !== 'string') {
    return null;
  }
  const normalized = severity.toLowerCase();
  if (normalized === 'warn') {
    return 'warning';
  }
  if (normalized === 'ignore' || normalized === 'warning' || normalized === 'error') {
    return normalized;
  }
  return null;
};

const normalizeError = (error) => {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'string') {
    return new Error(error);
  }
  if (error && typeof error === 'object') {
    const message = getMessageFromError(error, 'Unknown frontend error');
    const normalizedError = new Error(message);
    if (typeof error.name === 'string') {
      normalizedError.name = error.name;
    }
    if (typeof error.status === 'number') {
      normalizedError.status = error.status;
    }
    if (typeof error.statusText === 'string') {
      normalizedError.statusText = error.statusText;
    }
    if (Array.isArray(error.apiErrors)) {
      normalizedError.apiErrors = error.apiErrors;
    }
    return normalizedError;
  }
  return new Error('Unknown frontend error');
};

const shouldHandleErrors = () => typeof window !== 'undefined' && isDatadogEnabled();

const classifyError = (error, context = {}) => {
  const explicitSeverity = normalizeSeverity(context.severity);
  if (explicitSeverity) {
    return explicitSeverity;
  }

  const message = getMessageFromError(error, context.message || '');

  if (isIgnoredDatadogErrorMessage(message)) {
    return 'ignore';
  }

  if (isWarningDatadogMessage(message)) {
    return 'warning';
  }

  return 'error';
};

const getStatusCode = (error) => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  if (typeof error.status === 'number') {
    return error.status;
  }

  if (Array.isArray(error.apiErrors)) {
    const [firstApiError] = error.apiErrors;
    if (firstApiError && typeof firstApiError.status === 'number') {
      return firstApiError.status;
    }
  }

  return null;
};

const resolveBusinessSeverity = (error) => {
  const statusCode = getStatusCode(error);
  if (statusCode && statusCode >= 400 && statusCode < 500) {
    return 'warning';
  }
  return 'error';
};

export const captureBusinessError = (error, code, context = {}) => {
  if (!isDatadogEnabled() || !CAPTURED_BUSINESS_ERROR_CODES.has(code)) {
    return;
  }

  const statusCode = getStatusCode(error);

  captureFrontendError(normalizeError(error), {
    ...context,
    source: 'business',
    errorChannel: 'business',
    businessCode: code,
    handled: true,
    severity: resolveBusinessSeverity(error),
    statusCode: statusCode || undefined,
  });
};

const handleWindowError = (event) => {
  if (!event) {
    return;
  }
  const error = normalizeError(event.error || event.message || 'Window error');
  captureFrontendError(error, {
    source: 'window.error',
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
};

const handleUnhandledRejection = (event) => {
  const error = normalizeError(event?.reason || 'Unhandled promise rejection');
  captureFrontendError(error, { source: 'window.unhandledrejection' });
};

export const registerGlobalErrorHooks = () => {
  if (!shouldHandleErrors() || hooksRegistered) {
    return;
  }

  const win = window;
  win.addEventListener('error', handleWindowError);
  win.addEventListener('unhandledrejection', handleUnhandledRejection);
  hooksRegistered = true;
};

export const captureFrontendError = (error, context = {}) => {
  if (!isDatadogEnabled()) {
    return;
  }

  const severity = classifyError(error, context);
  if (severity === 'ignore') {
    return;
  }

  const { rum, logs } = ensureDatadogClient();
  const metadata = {
    ...context,
    severity,
  };

  if (severity === 'error' && rum && typeof rum.addError === 'function') {
    rum.addError(error, metadata);
  }

  if (severity === 'warning' && logs && logs.logger && typeof logs.logger.warn === 'function') {
    logs.logger.warn(error.message || 'frontend-warning', metadata, error);
  }

  if (severity === 'error' && logs && logs.logger && typeof logs.logger.error === 'function') {
    logs.logger.error(error.message || 'frontend-error', metadata, error);
  }
};
